/**
 * Run notices — the brief's "never a silent partial result".
 *
 * The backend attaches a typed `Notice` to a run and to its report whenever the
 * run is not the plain, complete thing it looks like: the FADP interlock
 * blocked the model step, the API was down and the run degraded to
 * deterministic-only, the Gemeinde in the dossier disagrees with the one that
 * was picked, no rulebase pack exists, zero rules resolved, the token budget
 * tripped, pages were scans.
 *
 * Two rules:
 *
 * 1. **Nothing is filtered.** This component renders every notice it is handed,
 *    in order. A code this frontend has never heard of still renders — with its
 *    message, its stage and its code — because the alternative is a degraded run
 *    that looks clean, which is the one failure mode the brief calls out by
 *    name. The icon map is a *lookup with a fallback*, never a gate.
 * 2. **A warning looks like a warning.** `kind: "warning"` gets the amber
 *    treatment; `kind: "info"` the navy one. These are not findings and never
 *    join the findings list — they carry no severity word, they render as
 *    full-width rows outside `[data-testid="hard-findings"]`, and a test asserts
 *    exactly that.
 */
import {
  Ban,
  CircleHelp,
  Coins,
  FileWarning,
  Info,
  ListChecks,
  MapPinOff,
  RefreshCw,
  ScanEye,
  ShieldAlert,
  TriangleAlert,
  Unplug,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { Icon } from '@/components/Icon'
import { useI18n } from '@/i18n'
import { cn } from '@/lib/utils'
import type { Notice } from '@/api/types'

/**
 * A glyph per code we know about. Deliberately a `Partial<…>`: this map is
 * decoration, not a whitelist. An unmapped or unknown code falls back to the
 * kind's own glyph and still renders in full.
 */
const CODE_ICON: Partial<Record<string, LucideIcon>> = {
  llm_blocked: ShieldAlert,
  llm_unavailable: Unplug,
  llm_degraded: Unplug,
  llm_values_rejected: ScanEye,
  pruefhinweise: ScanEye,
  token_budget_exceeded: Coins,
  workspace_day_budget_exceeded: Coins,
  no_rulebase_pack: FileWarning,
  zone_missing: FileWarning,
  zone_not_in_pack: FileWarning,
  gemeinde_mismatch: MapPinOff,
  zero_rules_resolved: Ban,
  regime_skips: ListChecks,
  hoehenregime_skips: ListChecks,
  raster_pdf: ScanEye,
  missing_inputs: ListChecks,
  deterministic_recheck: RefreshCw,
  facts_reused: RefreshCw,
  duplicate_upload: FileWarning,
}

const KIND_FALLBACK: Record<string, LucideIcon> = {
  warning: TriangleAlert,
  info: Info,
}

const KIND_CLASS: Record<string, string> = {
  warning: 'border-signal-amber/30 bg-signal-amber-soft text-signal-amber-text',
  info: 'border-signal-info/30 bg-signal-info-soft text-signal-info',
}

export function NoticeList({
  notices,
  title,
  testId = 'run-notices',
}: {
  notices: readonly Notice[] | null | undefined
  title?: string
  testId?: string
}) {
  const { t } = useI18n()
  const items = notices ?? []
  if (items.length === 0) return null

  return (
    <section className="flex flex-col gap-3" aria-label={title ?? t('notices.title')}>
      <h2 className="font-display text-[17px] font-semibold">{title ?? t('notices.title')}</h2>
      <ul data-testid={testId} className="flex flex-col gap-2">
        {items.map((notice, index) => {
          // Lookup with a fallback — never a gate. An unknown code keeps its
          // message, its stage and its code, and only loses its custom glyph.
          const glyph = CODE_ICON[notice.code] ?? KIND_FALLBACK[notice.kind] ?? CircleHelp
          const tone = KIND_CLASS[notice.kind] ?? KIND_CLASS.info
          return (
            <li
              key={`${notice.code}-${index}`}
              data-testid="notice"
              data-notice-code={notice.code}
              data-notice-kind={notice.kind}
              className={cn(
                'flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px]',
                tone,
              )}
            >
              <Icon icon={glyph} className="mt-0.5" />
              <div className="flex min-w-0 flex-col gap-1">
                <span>{notice.message_de}</span>
                {/* No `opacity` on text: a faded amber on amber-soft drops
                    below AA, and axe catches it. Smaller, not faded. */}
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
                  {notice.stage ? (
                    <span>
                      {t('notices.stage')}: {notice.stage}
                    </span>
                  ) : null}
                  <span className="num">{notice.code}</span>
                </span>
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
